// Staged columns of a HOSTED database (task O2c; backend: routers/column_stages.py,
// operate/stages.py). A migration adds a required column optional (or leaves a column that
// became required optional) and records it as a stage; people fill it (an import in mode
// "append and update by primary key", or the row form) and a project manager tightens it here
// (POST .../dbdesign/stages/{id}/tighten -> NOT NULL; 409 while a row is still empty). The list
// (GET .../dbdesign/stages) shows each stage's live fill count: "region: added, 2 of 3 rows filled".
// Nothing is shown while there is no stage (or the list cannot be read).
import { useCallback, useEffect, useState } from "react";
import { api } from "../../api";
import { Badge, Btn } from "../../components/Shared";
import { ErrorNote } from "../../components/Feedback";

const STATE_COLOR = { added: "amber", tightened: "green" };
const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" };

export function fillText(s) {
  if (s.error) return `fill count not available (${s.error})`;
  if (s.total == null) return "fill count not available";
  return `${s.filled} of ${s.total} row${s.total === 1 ? "" : "s"} filled`;
}

export default function ColumnStagesPanel({ projectId, canManage, refreshKey }) {
  const [stages, setStages] = useState([]);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/dbdesign/stages`);
      setStages((r && r.stages) || []);
    } catch {
      setStages([]);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (stages.length === 0) return null;

  const tighten = async (s) => {
    setBusy(s.id);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/dbdesign/stages/${s.id}/tighten`);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
      load();
    }
  };

  return (
    <section aria-label="Staged columns" data-testid="column-stages" style={{ marginTop: 16, fontSize: 13 }}>
      <h3 style={{ fontSize: 15, margin: "0 0 4px" }}>Staged columns</h3>
      <p style={{ margin: "0 0 6px", color: "#374151" }}>
        A required column starts optional in a hosted database. Fill it (an import in mode
        "append and update by primary key", or the row form), then tighten it: it becomes NOT NULL.
      </p>
      <ErrorNote error={error} />
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {stages.map(s => (
          <li key={s.id} style={{ padding: "6px 0", borderTop: "1px solid #f3f4f6", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span><strong>{s.table}.{s.column}</strong>: </span>
            <Badge color={STATE_COLOR[s.state] || "gray"}>{s.state}</Badge>
            <span>{fillText(s)}</span>
            {canManage && s.state === "added" && (
              <Btn variant="secondary" disabled={busy !== null} onClick={() => tighten(s)}>
                Tighten<span style={srOnly}> {s.table}.{s.column}</span>
              </Btn>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
